import { useNavigation } from '@react-navigation/native';
import { IpadLang, Lang } from '@w3lcome/types';
import { Button, Header, HTMLRender, Text } from '_/components';
import ContainerWidthLimit from '_/components/ContainerWidthLimit';
import { colors, fontSizes } from '_/config/theme';
import { textReplace } from '_/helpers/textReplace';
import { useCustomization } from '_/hooks/CustomizationContext';
import { useVisit } from '_/hooks/VisitContext';
import { HomeProps } from '_/navigation/types';

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View, StyleSheet } from 'react-native';

import { useCentralizedValuesFallback } from '_/hooks/useCentralizedValuesFallback';

const CheckinErrorScreen: React.FC = () => {
  const { customization } = useCustomization();

  const { visit, navigateToHome } = useVisit();

  const { t, i18n } = useTranslation();
  const navigation = useNavigation<HomeProps>();
  const [isBackToHomeEnabled, setIsBackToHomeEnabled] = useState(false);

  const { selectedOrFallbackCompany } = useCentralizedValuesFallback();

  const ipadCheckinErrorMessage = selectedOrFallbackCompany?.visitsConfig?.ipadCheckinErrorMessage;

  const getMessageByLang = (message: { [key in Lang]?: string } | undefined) => {
    if (!message) return '';

    if (typeof message === 'string') return message;

    const langValue =
      message[i18n.language as Lang] ||
      message[Lang.EN] ||
      message[Object.keys(message)[0] as Lang];

    return (langValue || '').trim();
  };

  useEffect(() => {
    const enableButtonTimerId = setTimeout(() => {
      setIsBackToHomeEnabled(true);
    }, 3 * 1000);

    return () => {
      clearTimeout(enableButtonTimerId);
    };
  }, []);

  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout>;

    timerId = setTimeout(() => {
      navigateToHome(navigation);
    }, 8 * 1000);

    return () => {
      clearTimeout(timerId);
    };
  }, []);

  const message = useMemo(() => {
    const welcomeMessage = getMessageByLang(ipadCheckinErrorMessage);
    return textReplace(welcomeMessage || '', i18n.language as IpadLang, customization, visit);
  }, [customization, visit]);

  return (
    <View style={styles.container}>
      <ContainerWidthLimit>
        <Header hideReturnButton />
        <View style={styles.content}>
          <View pointerEvents="none">
            <HTMLRender html={message} />
          </View>
        </View>
        <Button
          disabled={!isBackToHomeEnabled}
          onPress={() => navigateToHome(navigation)}
          containerStyles={styles.buttonContainer}
          buttonStyles={styles.button}
        >
          <Text
            fontType="bold"
            size={fontSizes.lg}
            color={isBackToHomeEnabled ? colors.white : colors.dark1}
          >
            {t('checkinScreen.backToHome', { defaultValue: 'Voltar ao início' })}
          </Text>
        </Button>
      </ContainerWidthLimit>
    </View>
  );
};

export default CheckinErrorScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
    alignItems: 'center',
  },
  content: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-evenly',
    paddingHorizontal: 64,
    textAlign: 'center',
  },
  buttonContainer: {
    width: '60%',
    alignSelf: 'center',
    marginBottom: 48,
  },
  button: {
    height: 64,
  },
});
